// Allow-list HTML sanitiser for the one place the app renders HTML it did not build itself:
// the AI content preview. Everything else in the app goes through React, which escapes by default.
//
// Allow-list, never block-list. A block-list is a list of the attacks someone has thought of;
// browsers keep inventing new ways to run script, and the list is always one step behind. This
// keeps a small set of formatting tags and drops everything else, so a tag nobody has heard of is
// removed precisely because nobody has heard of it.
//
// It parses with DOMParser rather than a regular expression. HTML is not a regular language, and
// every regex-based sanitiser eventually loses to a malformed tag the parser interprets differently
// from the regex. Parsing first means we walk the same tree the browser would build.
//
// Content-Security-Policy already blocks inline script on this origin, so this is the second lock
// on the same door, not the only one.

const ALLOWED_TAGS = new Set([
  'p', 'br', 'hr', 'div', 'span',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'strong', 'b', 'em', 'i', 'u', 's', 'sub', 'sup', 'small',
  'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption',
  'a', 'img', 'figure', 'figcaption',
]);

// Per-tag attribute allow-list. Nothing global: `style` is excluded deliberately, since CSS can
// load and position content, and an AI writing a blog post has no need of it.
const ALLOWED_ATTRS = {
  a: ['href', 'title'],
  img: ['src', 'alt', 'title', 'width', 'height'],
  td: ['colspan', 'rowspan'],
  th: ['colspan', 'rowspan', 'scope'],
};

// A URL is safe if it cannot execute. `javascript:` is the obvious one; `data:` is the subtle one,
// because data:text/html runs script in the document's own origin.
function safeUrl(value, { allowData = false } = {}) {
  const v = String(value || '').trim();
  // Strip characters a browser ignores but a naive check does not — "java\tscript:" still runs.
  const bare = v.replace(/[ - ]/g, '').toLowerCase();
  if (bare.startsWith('javascript:') || bare.startsWith('vbscript:')) return null;
  if (bare.startsWith('data:')) return allowData && /^data:image\/(png|jpe?g|gif|webp|avif);base64,/i.test(bare) ? v : null;
  return v;
}

export function sanitizeHtml(dirty) {
  if (!dirty || typeof dirty !== 'string') return '';
  let doc;
  try { doc = new DOMParser().parseFromString(dirty, 'text/html'); }
  catch { return ''; }   // unparseable input renders as nothing rather than as a guess

  const walk = (node) => {
    // Iterate over a copy: the loop removes children as it goes.
    for (const child of [...node.childNodes]) {
      if (child.nodeType === 3) continue;                 // text — always fine, it cannot execute
      if (child.nodeType !== 1) { child.remove(); continue; }  // comments, CDATA, processing instructions

      const tag = child.tagName.toLowerCase();
      if (!ALLOWED_TAGS.has(tag)) {
        // Unwrap rather than delete, so disallowed formatting does not silently eat the words
        // inside it — except for the tags whose whole point is to carry code.
        if (tag === 'script' || tag === 'style' || tag === 'iframe' || tag === 'object'
            || tag === 'embed' || tag === 'form' || tag === 'svg' || tag === 'math') {
          child.remove();
          continue;
        }
        while (child.firstChild) child.parentNode.insertBefore(child.firstChild, child);
        child.remove();
        continue;
      }

      const allowed = ALLOWED_ATTRS[tag] || [];
      for (const attr of [...child.attributes]) {
        const name = attr.name.toLowerCase();
        // Every on* handler goes, whatever the tag: onclick, onerror, onanimationend and the rest.
        if (!allowed.includes(name)) { child.removeAttribute(attr.name); continue; }
        if (name === 'href' || name === 'src') {
          const url = safeUrl(attr.value, { allowData: name === 'src' && tag === 'img' });
          if (url === null) child.removeAttribute(attr.name);
          else child.setAttribute(attr.name, url);
        }
      }
      // A link that leaves the app opens in a new tab without handing it a window reference.
      if (tag === 'a' && child.getAttribute('href')) {
        child.setAttribute('rel', 'noopener noreferrer nofollow');
        child.setAttribute('target', '_blank');
      }
      walk(child);
    }
  };

  walk(doc.body);
  return doc.body.innerHTML;
}

export default sanitizeHtml;
